import { StatsInfo } from "src/components/compounds/GameStats/stat-info-type";
import { percentFormatter, fourDigitNumberFormatter, decimalFormatter, eventDateFormatter } from "../formatters";
import { GridItem, PlayerWithLinkColumn, overrideColumn } from "../common-columns";

export interface BattingAdvancedFormattedRow {
  player: {
    id: string;
    teamID: string;
    name: string;
    status: string;
  };
  GP: number;
  PA: number;
  AB: number;
  QAB: number;
  "QAB%": number;
  "PA/BB": number;
  "BB/K": number;
  "C%": number;
  HHB: number;
  "LD%": number;
  "FB%": number;
  "GB%": number;
  BABIP: number;
  "BA/RISP": number;
  LOB: number;
  _2OUTRBI: number;
  XBH: number;
  TB: number;
  PS: number;
  "PS/PA": number;
  "2S+3": number;
  "6+": number;
  "AB/HR": number;
  GIDP: number;
  GITP: number;
  CI: number;
}

export const battingAdvancedGridDataFormatted = (grid: GridItem[]): BattingAdvancedFormattedRow[] => {
  return grid
    .filter(({ offense }) => offense)
    .map(({ person_id, team_id, name, eventID, date, offense }) => {
      return {
        player: {
          id: person_id,
          teamID: team_id,
          name,
          eventID,
          status: "active",
        },
        // Read by the per-game Date column; undefined (and ignored) in season/box-score views.
        event: date,
        GP: offense.gp,
        PA: offense.pa,
        AB: offense.ab,
        QAB: offense.qab,
        "QAB%": offense["QAB%"],
        "PA/BB": offense["PA/BB"],
        "BB/K": offense["BB/K"],
        "C%": offense["C%"],
        HHB: offense.hard,
        "LD%": offense["LND%"],
        "FB%": offense["FLB%"],
        "GB%": offense["GB%"],
        BABIP: offense.babip,
        "BA/RISP": offense["BA/RISP"],
        LOB: offense.lob,
        _2OUTRBI: offense._2OUTRBI,
        XBH: offense.xbh,
        TB: offense.tb,
        PS: offense.ps,
        "PS/PA": offense["PS/PA"],
        "2S+3": offense["2S+3"],
        "6+": offense["6+"],
        "AB/HR": offense["AB/HR"],
        GIDP: offense.gidp,
        GITP: offense.gitp,
        CI: offense.ci,
      };
    });
};

export const CoreBattingAdvanced: StatsInfo[] = [
  { name: "PA", field: "PA", def: "Plate appearances" },
  { name: "AB", field: "AB", def: "At bats" },
  {
    name: "QAB",
    field: "QAB",
    def: "Quality at bats (any one of: 3 pitches after 2 strikes, 6+ pitch ABs, XBH, HHB, BB, SAC Bunt, SAC Fly)",
  },
  {
    name: "QAB%",
    field: "QAB%",
    def: "Quality at bats per plate appearance",
    valueFormatter: percentFormatter,
    sort: "desc",
  },
  {
    name: "PA/BB",
    field: "PA/BB",
    def: "Plate appearances per walk",
    valueFormatter: decimalFormatter(1),
  },
  {
    name: "BB/K",
    field: "BB/K",
    def: "Walks per strikeout",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "C%",
    field: "C%",
    def: "Contact percentage/Contact rate: AB-K/AB",
    valueFormatter: percentFormatter,
  },
  { name: "HHB", field: "HHB", def: "Hard hit balls: Total line drives and hard ground balls" },
  { name: "LD%", field: "LD%", def: "Line drive percentage", valueFormatter: percentFormatter },
  { name: "FB%", field: "FB%", def: "Fly ball percentage", valueFormatter: percentFormatter },
  { name: "GB%", field: "GB%", def: "Ground ball percentage", valueFormatter: percentFormatter },
  {
    name: "BABIP",
    field: "BABIP",
    def: "Batting average on balls in play",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "BA/RISP",
    field: "BA/RISP",
    def: "Batting average with runners in scoring position",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "LOB", field: "LOB", def: "Runners left on base" },
  //   { name: "2OUTRBI", field: "2OUTRBI", def: "2-out RBI" },
  { name: "XBH", field: "XBH", def: "Extra-base hits" },
  { name: "TB", field: "TB", def: "Total bases" },
  { name: "PS", field: "PS", def: "Pitches seen" },
  {
    name: "PS/PA",
    field: "PS/PA",
    def: "Pitches seen per plate appearance",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "2S+3", field: "2S+3", def: "Plate appearances in which batter sees 3+ pitches after 2 strikes" },
  {
    name: "2S+3%",
    field: "2S+3%",
    def: "% of plate appearances in which batter sees 3+ pitches after 2 strikes",
    valueFormatter: percentFormatter,
  },
  { name: "6+", field: "6+", def: "Plate appearances with 6+ pitches" },
  {
    name: "6+%",
    field: "6+%",
    def: "% of plate appearances of 6+ pitches",
    valueFormatter: percentFormatter,
  },
  {
    name: "AB/HR",
    field: "AB/HR",
    def: "At bats per home run",
    valueFormatter: decimalFormatter(1),
  },
  { name: "GIDP", field: "GIDP", def: "Hit into double play" },
  { name: "GITP", field: "GITP", def: "Hit into triple play" },
  { name: "CI", field: "CI", def: "Batter advances on catcher's interference" },
];

export const BattingAdvanced: StatsInfo[] = (
  [PlayerWithLinkColumn, { name: "GP", field: "GP", def: "Games played" }] as StatsInfo[]
).concat(CoreBattingAdvanced);

export const EventBattingAdvanced: StatsInfo[] = (
  [
    {
      name: "Date",
      field: "event",
      valueFormatter: eventDateFormatter,
      columnProps: {
        width: 100,
        pinned: true,
        cellStyle: { textAlign: "left" },
        sort: "asc",
      },
    },
    {
      name: "OPPONENT",
      field: "opponent",
      columnProps: {
        width: 260,
        cellStyle: { textAlign: "left" },
        headerClass: "left-aligned-column-header",
      },
    },
  ] as StatsInfo[]
).concat(CoreBattingAdvanced);

overrideColumn(EventBattingAdvanced, "QAB%", {
  name: "QAB%",
  field: "QAB%",
  def: "Quality at bats per plate appearance",
  valueFormatter: percentFormatter,
});

const excludedStats = [
  "QAB%",
  "PA/BB",
  "BB/K",
  "C%",
  "LD%",
  "FB%",
  "GB%",
  "2S+3%",
  "6+%",
  "SAC",
  "AB/HR",
  "GITP",
  "SF",
];

export const GameBattingAdvanced: StatsInfo[] = ([PlayerWithLinkColumn] as StatsInfo[]).concat(
  [...CoreBattingAdvanced].filter((stat) => !excludedStats.includes(stat.name))
);

overrideColumn(GameBattingAdvanced, "QAB", {
  name: "QAB",
  field: "QAB",
  def: "Quality at bats (any one of: 3 pitches after 2 strikes, 6+ pitch ABs, XBH, HHB, BB, SAC Bunt, SAC Fly)",
  columnProps: { sort: "desc" },
});

const ReplacedPercentageStatsBattingAdvanced: StatsInfo[] = [
  { name: "LD", field: "LD", def: "Line drives" },
  { name: "FB", field: "FB", def: "Fly balls" },
  { name: "GB", field: "GB", def: "Ground balls" },
];

GameBattingAdvanced.splice(5, 0, ...ReplacedPercentageStatsBattingAdvanced);
